import React from "react";
import { useTranslation } from "react-i18next";
import type { MorphologyParts, MorphologySegment, SegmentKind } from "@/lib/philology/api";

interface MorphemeBreakdownProps {
  word: string;
  morphology?: MorphologyParts | null;
  size?: "sm" | "md" | "lg";
  showLabels?: boolean;
  showLegend?: boolean;
  className?: string;
}

const KIND_COLOR: Record<SegmentKind, { bg: string; text: string; border: string; label: string }> = {
  prefix: { bg: "bg-rose-50 dark:bg-rose-500/20", text: "text-rose-700 dark:text-rose-200", border: "border-rose-300 dark:border-rose-500/60", label: "text-rose-600 dark:text-rose-300" },
  root: { bg: "bg-emerald-50 dark:bg-emerald-500/20", text: "text-emerald-700 dark:text-emerald-200", border: "border-emerald-300 dark:border-emerald-500/60", label: "text-emerald-600 dark:text-emerald-300" },
  suffix: { bg: "bg-violet-50 dark:bg-violet-500/20", text: "text-violet-700 dark:text-violet-200", border: "border-violet-300 dark:border-violet-500/60", label: "text-violet-600 dark:text-violet-300" },
  ending: { bg: "bg-amber-50 dark:bg-amber-500/20", text: "text-amber-700 dark:text-amber-200", border: "border-amber-300 dark:border-amber-500/60", label: "text-amber-600 dark:text-amber-300" },
  infix: { bg: "bg-cyan-50 dark:bg-cyan-500/20", text: "text-cyan-700 dark:text-cyan-200", border: "border-cyan-300 dark:border-cyan-500/60", label: "text-cyan-600 dark:text-cyan-300" },
  linker: { bg: "bg-slate-50 dark:bg-slate-500/20", text: "text-slate-700 dark:text-slate-200", border: "border-slate-300 dark:border-slate-500/60", label: "text-slate-600 dark:text-slate-300" },
};

export default function MorphemeBreakdown({
  word,
  morphology,
  size = "md",
  showLabels = false,
  showLegend = false,
  className,
}: MorphemeBreakdownProps) {
  const { t } = useTranslation();
  const segments = useValidSegments(word, morphology);
  const sizeCls = size === "sm" ? "text-base" : size === "lg" ? "text-3xl md:text-4xl" : "text-xl";
  const padCls = size === "sm" ? "px-1 py-0.5" : "px-1.5 py-0.5";

  if (!segments || segments.length === 0) {
    return (
      <div className={`${sizeCls} font-semibold text-[hsl(var(--foreground))] ${className ?? ""}`}>
        {word || "—"}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-2 ${className ?? ""}`}>
      <div className="flex flex-wrap items-baseline gap-y-2 leading-tight" role="group" aria-label={t("philology.morphology")}>
        {segments.map((seg, i) => {
          const colors = KIND_COLOR[seg.kind];
          return (
            <span key={i} className="flex flex-col items-center" style={{ marginRight: 2 }}>
              <span
                className={`${sizeCls} ${padCls} font-bold rounded-md border ${colors.bg} ${colors.text} ${colors.border}`}
                title={t(`philology.${seg.kind}`, { defaultValue: seg.kind })}
              >
                {seg.text}
              </span>
              {showLabels && (
                <span className={`mt-1 text-[10px] uppercase tracking-wide font-medium ${colors.label}`}>
                  {t(`philology.${seg.kind}`, { defaultValue: seg.kind })}
                </span>
              )}
            </span>
          );
        })}
      </div>
      {showLegend && <Legend usedKinds={segments.map((s) => s.kind)} />}
    </div>
  );
}

function useValidSegments(word: string, morphology?: MorphologyParts | null): MorphologySegment[] | null {
  if (!morphology) return null;
  const segments = morphology.segments;
  if (!Array.isArray(segments) || segments.length === 0) return null;
  const concatenated = segments.map((s) => s.text).join("");
  if (concatenated.toLowerCase() !== word.toLowerCase()) {
    return null;
  }
  return segments;
}

function Legend({ usedKinds }: { usedKinds: SegmentKind[] }) {
  const { t } = useTranslation();
  const unique = Array.from(new Set(usedKinds));
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] mt-1">
      {unique.map((kind) => {
        const colors = KIND_COLOR[kind];
        return (
          <span key={kind} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--bg3))] text-[hsl(var(--muted-foreground))]">
            <span className={`inline-block w-2 h-2 rounded-sm ${colors.bg} border ${colors.border}`} aria-hidden />
            {t(`philology.${kind}`, { defaultValue: kind })}
          </span>
        );
      })}
    </div>
  );
}
